<template>
  <b-container class="chat-box cm-demo">
    <b-modal
      ref="login-cta"
      centered
      hide-footer
      hide-header
      content-class="login-cta-modal"
    >
      <div class="text-center p-3">
        <h4 class="font-weight-bold mb-3">
          ¡Inicia sesión para crear tus propios materiales!
        </h4>
        <p class="text-muted">
          Este es un material de ejemplo, generado de verdad para
          {{ contextoCapturado }}. Con tu cuenta eliges el curso, la asignatura y
          los objetivos que necesites, y descargas el PDF listo para imprimir.
        </p>
        <p v-if="bonusHtml" class="mb-3" v-html="bonusHtml"></p>
        <b-button pill variant="primary" size="lg" @click="login">
          Iniciar sesión
        </b-button>
      </div>
    </b-modal>

    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <div
          v-for="(msg, msgidx) in messages"
          :key="`${demoRunId}-${msgidx}`"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
              '--product-deep': '#3e175e',
              '--product-rgb': '145, 74, 207',
              '--product-soft': '#f7f0fc',
              '--product-line': '#e3d4f0',
            }"
            :initMsg="msg"
            :key="`${demoRunId}-${msgidx}`"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            :condition="condition"
            @select-card="handleCardClick"
            @continue="continueChat"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
            @go-to-step="goToStep"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// Vista sin sesión de "Materiales para la clase". Se llega haciendo clic en el
// teaser del producto (Default.changeView empuja `class-materials-demo` cuando
// no hay token).
//
// Tiene la misma forma que PlanificationDemo: un chat con un menú de opciones, y
// el demo detrás de una de ellas. Antes esto era una tarjeta estática con un
// botón de login; la diferencia importa porque quien llega de un anuncio no
// necesariamente quiere ver el demo — puede querer los detalles del servicio o
// entrar directo.
//
// Lo que se muestra NO es una copia de la interfaz: el paso del demo monta
// `MaterialAiGenerate`, el componente de producción, y le inyecta un origen de
// datos de fixture. El fixture lo escribe `capturar_demo_materiales`, así que el
// contenido son ejercicios generados de verdad. Ninguna llamada a IA ni a la API
// se dispara acá (los visitantes sin sesión pueden ser bots).
//
// El origen viaja en `context.demo`, que es la misma costura que usa
// PlanificationDemo: `ChatMessageComponent` le pasa props fijas a un
// `custom-component` y `dataSource` no está entre ellas.
import ChatMessageComponent from "./ChatMessageComponent.vue";
import MaterialAiGenerate from "./MaterialAiGenerate.vue";
import cargarFixture, {
  asignaturaCapturada,
  cursoCapturado,
  preguntasPorOa,
} from "./materialsDemoFixture";
import { crearOrigenDemo } from "./materialsDemoDataSource";
import { crearAutoplayMateriales } from "./materialsDemoAutoplay";
import { fetchRewards, applyBonus, bonusLine } from "./signupRewards";
import { scrollToElementTop, scrollToPageBottom } from "@/utils/scrollToTop";
import {
  medirDemoIniciada,
  medirDemoTerminada,
} from "@/services/analytics";

// Pasos del chat donde, si el visitante no elige nada, el demo se elige a sí
// mismo — y a qué opción salta. Los CTA del final (`closingMessage`) quedan
// fuera a propósito: ahí la decisión es suya, y elegirle el login sería empujón
// de otra cosa.
const EMPUJON_DEL_MENU = {
  welcomeMenu: "demoIntro",
  menu: "demoIntro",
};

// Cuánto espera el menú antes de elegir solo. Más de lo que espera cada paso
// del wizard: acá hay tres opciones que leer, y es la primera pantalla.
const ESPERA_MENU = 6000;

export default {
  name: "MaterialesClaseDemo",
  components: { ChatMessageComponent },
  // Absorbe las props que el layout le ata al router-view, para que no se
  // filtren al elemento raíz; el demo no usa ninguna.
  props: {
    bannerLoaded: { type: Boolean, required: false, default: false },
    routeName: { type: String, required: false, default: "" },
    tags: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      mainColor: "#914acf",
      secondaryColor: "#6f2da8",
      currentQuestionIndex: "",
      condition: "",
      context: {},
      messages: [],
      // Se incrementa en cada replay para que cambien las claves del v-for y Vue
      // vuelva a montar el chat. Con claves por índice reusaría las instancias y
      // el wizard se quedaría en el paso donde lo dejaron.
      demoRunId: 0,
      // Usos gratis + misiones, con los números reales de la config.
      rewards: null,
      // Lo que publicó la última captura en ESTE servidor, o null si todavía no
      // se corrió ninguna acá.
      fixture: null,
      origen: null,
      questions: {},
      // Empujones pendientes: el demo avanza solo si el visitante no lo hace.
      demoTimers: [],
      // La demostracion se cuenta una vez por montaje: el replay del
      // cierre la corre de nuevo y no es una visita nueva.
      inicioContado: false,
      finContado: false,
    };
  },
  created() {
    // El contenido del demo NO viene en el bundle: lo publica en el servidor
    // `capturar_demo_materiales` y se pide acá. Se espera junto con los usos
    // gratis para abrir el chat una sola vez, ya con el menú definitivo: si el
    // menú se pintara antes, la opción del demo aparecería —o desaparecería—
    // delante del visitante.
    Promise.all([
      cargarFixture(),
      fetchRewards("Materiales para la clase"),
    ]).then(([contenido, rewards]) => {
      this.fixture = contenido;
      this.rewards = rewards;
      if (contenido) {
        try {
          this.origen = this.nuevoOrigen();
        } catch (error) {
          // Sin origen, el wizard le pegaría al backend REAL con un visitante
          // sin sesión. Antes que eso, no se ofrece el demo.
          console.error("[MaterialesClaseDemo] fixture inválido", error);
          this.origen = null;
        }
      }
      this.questions = this.buildQuestions();
      this.applyRewards();
      this.startDemo();
    });
  },
  beforeDestroy() {
    this.clearDemoTimers();
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
    bonusHtml() {
      return bonusLine(this.rewards);
    },
    /**
     * Si este servidor tiene un demo que mostrar.
     *
     * Puede no tenerlo con todo el código desplegado: el contenido se publica
     * corriendo el comando de captura, no con el deploy. Cuando falta, la opción
     * desaparece del menú en vez de llevar a una pantalla vacía.
     */
    demoDisponible() {
      return Boolean(this.origen);
    },
    /**
     * Curso y asignatura salen del fixture, nunca escritos a mano: el comando de
     * captura se corre con los parámetros que se quieran y el demo se describe
     * solo. Si mañana se captura 5° básico · Ciencias, acá lo dice.
     */
    contextoCapturado() {
      const curso = cursoCapturado(this.fixture);
      const asignatura = asignaturaCapturada(this.fixture);
      const partes = [
        curso && curso.original_name,
        asignatura && asignatura.name,
      ].filter(Boolean);
      return partes.length ? partes.join(" · ") : "una clase de ejemplo";
    },
  },
  methods: {
    buildQuestions() {
      return {
        // Menú de entrada. Quien llega desde un anuncio elige qué quiere ver, en
        // vez de caer dentro del wizard sin contexto.
        welcomeMenu: {
          name: "welcomeMenu",
          text: `👋 ¡Hola! Con Utopie creas materiales para la clase con IA: eliges curso, asignatura y Objetivos de Aprendizaje, y obtienes las actividades listas para imprimir, dibujadas y con su pauta.<br/>
            <strong>¿Qué te gustaría hacer?</strong>`,
          type: "options",
          interactive: true,
          answersLoaded: true,
          single_choice: true,
          answers: [
            {
              name: "Iniciar sesión para probar la versión real",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            ...(this.demoDisponible
              ? [
                  {
                    name: "Ver el demo de un material",
                    isSelected: false,
                    class: "col-md-7 col-12 my-2",
                    bootstrap_classes: "text-left px-2 font-weight-bold",
                    nextQuestionIndex: "demoIntro",
                  },
                ]
              : []),
            {
              name: "Conocer más detalles del servicio",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              nextQuestionIndex: "details",
            },
          ],
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        demoIntro: {
          name: "demoIntro",
          text: `¡Perfecto! Este es el producto real: elige los objetivos y mira cómo se construye cada ejercicio. Las actividades que vas a ver las generó la IA para <strong>${this.contextoCapturado}</strong>, y las dibujó el mismo motor que imprime el PDF.`,
          type: "informative",
          nextQuestionIndex: "wizardDemo",
          answersLoaded: true,
          answers: [],
          // `getAnswers` no es opcional acá aunque no haya tarjetas que mostrar:
          // ChatMessageComponent sólo emite `continue` para un paso informativo
          // DENTRO de la rama de getAnswers. Sin él el chat se queda en este
          // globo y el wizard no llega a montarse.
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        // OJO con el nombre: `loadQuestion` hace `context[question.name] = {}`,
        // así que un paso llamado `demo` borraría `context.demo` —que es
        // justamente por donde viaja el origen de datos del fixture— y el wizard
        // se pondría a hablar con el backend REAL sin que nada avisara.
        wizardDemo: {
          name: "wizardDemo",
          text: "",
          type: "custom-component",
          answersLoaded: true,
          answers: [],
          dontShowNextButton: true,
          component: MaterialAiGenerate,
          // Tener getAnswers evita que ChatMessageComponent emita `continue` solo
          // en este paso: el chat avanza cuando el wizard termina el material.
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          nextQuestionIndex: "closingMessage",
          routine() {},
        },
        details: {
          name: "details",
          text: "¡Perfecto! Esto es lo que puedes generar con Materiales para la clase",
          type: "informative",
          nextQuestionIndex: "menu",
          answersLoaded: false,
          answers: [],
          process_dic: [
            {
              title: "Actividades dibujadas, no sólo texto",
              text: "Cada ejercicio se dibuja: rectas numéricas, relojes, fracciones, pictogramas, agrupaciones. El mismo motor que lo anima en pantalla es el que lo imprime, así que lo que ves es lo que sale en el papel.",
              image: require("@/assets/img/imagen-material-1.png"),
              height: "300",
            },
            {
              title: "Alineadas al currículum chileno",
              text: "Eliges el Objetivo de Aprendizaje y el indicador, y las actividades se generan para eso. Cobertura desde Pre-kínder en adelante.",
              image: require("@/assets/img/imagen-material-2.png"),
              height: "300",
            },
            {
              title: "Listas para imprimir",
              text: "El material se descarga en PDF, con su hoja de respuestas, listo para fotocopiar y llevar a la sala.",
              image: require("@/assets/img/landing_page/PNG/web/Materiales_clase.png"),
              height: "300",
            },
          ],
          getAnswers() {
            if (!this.answersLoaded) {
              this.answers = this.process_dic.map((ans) => ({
                title: ans.title,
                text: ans.text,
                image: ans.image,
                height: ans.height,
                class: "col-12 col-md-12 mb-2",
              }));
              this.answersLoaded = true;
            }
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        // Menú de vuelta, después de los detalles o del demo. Se reconstruye en
        // cada visita para que la opción ya usada no vuelva a ofrecerse.
        menu: {
          name: "menu",
          text: "¿Qué te gustaría hacer ahora?",
          type: "options",
          interactive: true,
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: [
            {
              name: "Iniciar sesión para probar la versión real",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            ...(this.demoDisponible
              ? [{ name: "Ver el demo de un material", step: "demoIntro" }]
              : []),
            { name: "Conocer más detalles del servicio", step: "details" },
          ],
          getAnswers() {
            if (!this.answersLoaded) {
              this.answers = this.process_dic
                .filter((ans) => !ans.step || !this.visitados.includes(ans.step))
                .map((ans) => ({
                  name: ans.name,
                  isSelected: false,
                  class: "col-md-7 col-12 my-2",
                  bootstrap_classes: "text-left px-2 font-weight-bold",
                  cardClass: ans.cardClass,
                  option: ans.option,
                  nextQuestionIndex: ans.step,
                }));
              this.answersLoaded = true;
            }
            return Promise.resolve(this.answers);
          },
          visitados: [],
          routine() {},
        },
        closingMessage: {
          name: "closingMessage",
          text: "Y así de simple: eliges los objetivos y el material sale listo para imprimir. ¿Quieres crear el tuyo, con tus propios cursos?",
          type: "options",
          interactive: true,
          answersLoaded: true,
          single_choice: true,
          answers: [
            {
              name: "Iniciar sesión y crear mi propio material",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Conocer más detalles del servicio",
              isSelected: false,
              class: "col-md-7 col-12 my-2",
              bootstrap_classes: "text-left px-2 font-weight-bold",
              nextQuestionIndex: "details",
            },
          ],
          getAnswers() {
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
      };
    },
    // Los menús prometen los usos gratis con el número real de la config.
    applyRewards() {
      applyBonus(this.questions, this.rewards, [
        "welcomeMenu",
        "menu",
        "closingMessage",
      ]);
    },
    startDemo(startStep = "welcomeMenu") {
      this.$store.commit("chatbot/clear");
      // `service`/`productMode` imitan lo que el chatbot real pone en su
      // contexto; `demo` lleva el origen de datos y es lo que pone a
      // MaterialAiGenerate en modo replay.
      this.context = {
        service: "Materiales para la clase",
        productMode: "class-materials",
        demo: this.origen,
      };
      this.condition = "";
      this.messages = [];
      this.currentQuestionIndex = startStep;
      const question = this.questions[this.currentQuestionIndex];
      this.context[question.name] = {};
      this.messages.push(question);
    },
    resetDemo(startStep = "demoIntro") {
      this.clearDemoTimers();
      this.demoRunId += 1;
      // El origen se consume: cada pregunta capturada se sirve una sola vez, así
      // que un replay con el mismo origen dejaría el wizard sin nada que mostrar.
      this.origen = this.nuevoOrigen();
      this.questions = this.buildQuestions();
      this.applyRewards();
      this.startDemo(startStep);
      window.scrollTo(0, 0);
    },
    /**
     * Un origen de datos nuevo, con el guión que recorre el wizard solo.
     *
     * El guión viaja pegado al origen porque los dos entran por la misma costura
     * (`context.demo`): `ChatMessageComponent` le pasa props fijas a un
     * `custom-component` y no hay por dónde más alcanzarle nada al wizard.
     */
    nuevoOrigen() {
      return {
        ...crearOrigenDemo(this.fixture),
        autoplay: crearAutoplayMateriales({
          pasoDeCierre: "closingMessage",
          // Cuántos ejercicios puede dar cada OA sin repetir ninguno. Es lo que
          // le permite al guión marcar los OAs que hagan falta para llenar la
          // hoja, en vez de un número fijo escrito acá.
          disponiblesPorOa: preguntasPorOa(this.fixture),
        }),
      };
    },
    addDemoTimer(fn, delay) {
      this.demoTimers.push(setTimeout(fn, delay));
    },
    clearDemoTimers() {
      this.demoTimers.forEach((t) => clearTimeout(t));
      this.demoTimers = [];
    },
    /**
     * Si el visitante no elige en unos segundos, el demo se elige a sí mismo.
     *
     * Mucha gente llega desde un anuncio y no hace clic en nada: se queda
     * mirando el menú hasta que se va. Elegirle la opción del demo es lo que la
     * pantalla vino a hacer, y cualquier clic suyo cancela el empujón.
     */
    programarEmpujon(question) {
      this.clearDemoTimers();
      if (!question || !EMPUJON_DEL_MENU[question.name]) return;
      const destino = EMPUJON_DEL_MENU[question.name];
      const ansidx = (question.answers || []).findIndex(
        (ans) => ans.nextQuestionIndex === destino
      );
      // El menú de vuelta ya no ofrece el demo si el visitante lo vio: ahí no
      // hay nada que empujar.
      if (ansidx < 0) return;
      const msgidx = this.messages.length - 1;
      this.addDemoTimer(() => {
        if (this.currentQuestion !== question) return;
        // Antes de marcar la tarjeta: contar algo que despues se rompe
        // es un dato de mas; marcar y no contar es un hueco.
        this.contarInicio("automatico");
        this.autoSelect(msgidx, ansidx);
        // Un respiro entre marcar la tarjeta y avanzar, para que se vea cuál se
        // eligió en vez de que la pantalla salte.
        this.addDemoTimer(() => this.continueChat(), 600);
      }, ESPERA_MENU);
    },
    // Cada clic en una tarjeta llega acá. Los CTA reales (login/replay) andan; en
    // los pasos guionados se ofrece el login en vez de dejar cambiar la selección.
    handleCardClick(answer, msgidx, ansidx) {
      // Si el visitante eligió, manda él.
      this.clearDemoTimers();
      if (answer && answer.option === "login") {
        this.login();
        return;
      }
      if (answer && answer.option === "replay") {
        this.resetDemo();
        return;
      }
      if (this.currentQuestion && this.currentQuestion.interactive) {
        if (this.messages[msgidx].name !== this.currentQuestion.name) return;
        if (answer && answer.nextQuestionIndex === "demoIntro") {
          this.contarInicio("boton");
        }
        this.autoSelect(msgidx, ansidx);
        this.continueChat();
        return;
      }
      this.showLoginCta();
    },
    showLoginCta() {
      this.$refs["login-cta"].show();
    },
    autoSelect(msgidx, ansidx) {
      const message = this.messages[msgidx];
      if (!message || !message.answers[ansidx]) return;
      if (message.single_choice) {
        message.answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      message.answers[ansidx].isSelected = true;
    },
    continueChat() {
      const question = this.currentQuestion;
      if (!question || question.isFinal) return;
      if (question.type === "options") {
        this.context[question.name] = question.single_choice
          ? question.answers.find((ans) => ans.isSelected) || {}
          : question.answers.filter((ans) => ans.isSelected);
      }
      this.messages.slice(-1)[0].answered = true;
      const selected = this.context[question.name];
      this.currentQuestionIndex =
        question.single_choice && selected && selected.nextQuestionIndex
          ? selected.nextQuestionIndex
          : question.nextQuestionIndex;
      this.loadQuestion(this.currentQuestionIndex);
    },
    loadQuestion(questionIndex) {
      try {
        const question = this.questions[questionIndex];
        if (!question) return;
        // El menú de vuelta se rearma cada vez, sin lo ya visto.
        if (question.name === "menu") {
          question.visitados = this.visitados();
          question.answersLoaded = false;
        }
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    /** Pasos que el visitante ya recorrió, para no volver a ofrecerlos. */
    visitados() {
      return this.messages.map((m) => m.name);
    },
    // Lo emite MaterialAiGenerate (lo repite ChatMessageComponent) cuando el
    // material quedó listo: el chat salta al cierre.
    goToStep(step) {
      if (!this.questions[step]) return;
      // El guion pide este salto cuando el material quedo listo: ese
      // es el final de la demostracion. El paso se lo damos nosotros
      // en `nuevoOrigen()` (`pasoDeCierre`).
      if (step === "closingMessage") this.contarFin();
      this.messages.slice(-1)[0].answered = true;
      this.currentQuestionIndex = step;
      this.loadQuestion(step);
    },
    contarInicio(metodo) {
      if (this.inicioContado) return;
      this.inicioContado = true;
      medirDemoIniciada("Materiales para la clase", metodo);
    },
    contarFin() {
      if (this.finContado) return;
      this.finContado = true;
      medirDemoTerminada("Materiales para la clase");
    },
    scrollToBottom() {
      scrollToPageBottom();
    },
    // Lo emite ChatMessageComponent apenas el mensaje terminó de tipearse y sus
    // tarjetas quedaron visibles: el momento en que el visitante puede elegir, y
    // por lo tanto el momento en que empieza a correr el reloj del empujón.
    scrollToMsg(name) {
      const msgRef = this.$refs[`msgRef-${name}`];
      if (msgRef && msgRef.length) {
        const el = msgRef.at(-1).$el;
        const parentMsg = el && el.parentNode;
        scrollToElementTop(parentMsg);
      }
      if (this.currentQuestion && this.currentQuestion.name === name) {
        this.programarEmpujon(this.currentQuestion);
      }
    },
    login() {
      this.$refs["login-cta"].hide();
      this.$emit("open-login");
    },
  },
};
</script>

<style scoped>
.cm-demo {
  --main-color: #914acf;
  --secondary-color: #6f2da8;
  --product-deep: #3e175e;
  --product-rgb: 145, 74, 207;
  --product-soft: #f7f0fc;
  --product-line: #e3d4f0;
  margin: 10px auto;
  width: 100%;
  padding-top: 1rem;
  padding-bottom: 2rem;
  font-family: "Ubuntu", Arial, sans-serif;
}
</style>
